import { useState, type ReactNode } from "react";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { EllipsisVertical } from "lucide-react";

export interface OverflowAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Red text, for actions that remove or delete. */
  destructive?: boolean;
  /** Kept from the desktop button so E2E selectors work at phone width. */
  testId?: string;
}

interface OverflowMenuProps {
  actions: OverflowAction[];
  /** Accessible name of the ⋮ button, e.g. "More actions for Winter Summit". */
  ariaLabel: string;
  testId?: string;
}

/**
 * ⋮ button that opens a menu of actions. On phones it holds the actions that
 * sit as buttons or icons on desktop: secondary page actions and per-row
 * actions. Clicks do not reach a clickable row underneath.
 */
export function OverflowMenu({ actions, ariaLabel, testId }: OverflowMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (actions.length === 0) return null;

  return (
    <>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        data-testid={testId}
      >
        <EllipsisVertical size={20} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            disabled={action.disabled}
            data-testid={action.testId}
            onClick={() => {
              setAnchor(null);
              action.onClick();
            }}
            sx={action.destructive ? { color: "error.main" } : undefined}
          >
            {action.icon && (
              <ListItemIcon sx={action.destructive ? { color: "error.main" } : undefined}>
                {action.icon}
              </ListItemIcon>
            )}
            <ListItemText primary={action.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
